import React, { useRef, useEffect } from "react";
import Card from "./Card";
import styles from './Card.module.css';

const CardContainer = ({ posts, loading, title = "Products", isWishlistPage = false, onProductClick }) => {
  const containerRef = useRef(null);
  let startX = 0;
  let scrollLeft = 0;
  let isDragging = false;
  let lastScrollLeft = 0;
  let lastScrollTime = Date.now();

  const getContainerStyle = () => {
    const isMobile = window.innerWidth <= 768;
    const shouldCenter = isMobile && posts && posts.length <= 2; // Center for 1 or 2 cards on mobile
    
    return {
      margin: '0 auto',
      padding: '16px 0',
      justifyContent: isMobile ? 'flex-start' : (posts && posts.length <= 4 ? 'center' : 'flex-start'),
      alignItems: 'stretch',
    };
  };

  // Enhanced Smooth Touch Scrolling
  const handleTouchStart = (e) => {
    startX = e.touches[0].pageX;
    scrollLeft = containerRef.current.scrollLeft;
    isDragging = true;
    containerRef.current.style.scrollBehavior = 'auto';
    lastScrollLeft = scrollLeft;
    lastScrollTime = Date.now();
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.touches[0].pageX;
    const walk = (x - startX) * 1.5;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    isDragging = false;
    containerRef.current.style.scrollBehavior = 'smooth';
    
    const container = containerRef.current;
    const isMobile = window.innerWidth <= 768;
    const cardWidth = isMobile ? 160 + 10 : 240 + 20;
    const currentScrollLeft = container.scrollLeft;
    const velocity = Math.abs(currentScrollLeft - lastScrollLeft) / (Date.now() - lastScrollTime);
    
    let nearestCard = Math.round(currentScrollLeft / cardWidth);
    
    // Add momentum based on velocity
    if (velocity > 0.5) {
      const direction = currentScrollLeft > lastScrollLeft ? 1 : -1;
      nearestCard += Math.round(velocity * direction);
    }
    
    const maxCards = Math.floor(container.scrollWidth / cardWidth) - 1;
    nearestCard = Math.max(0, Math.min(nearestCard, maxCards));
    
    container.scrollTo({
      left: nearestCard * cardWidth,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: false });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);

      return () => {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, []);

  return (
    <>
      <h1 style={{ position: 'relative', margin: '2% 5% -5% 5%'}} data-aos="fade-up" data-aos-delay="100">
        {typeof title === 'string' ? title : (title.name || 'Category')}
      </h1>
      <div className={styles.cardContainerWrapper} style={{ position: 'relative', width: '100%' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'contain',
          backgroundAttachment: 'fixed',
          opacity: 0.25,
          zIndex: 0,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            ref={containerRef}
            className={styles.cardContainer}
            style={getContainerStyle()}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {loading ? (
              <p data-aos="fade-in" data-aos-delay="300">Loading...</p>
            ) : posts && posts.length > 0 ? (
              posts.map((post, index) => (
                <Card
                  key={`${post._id}-${index}`}
                  post={post}
                  isWishlistPage={isWishlistPage}
                  onClick={() => onProductClick ? onProductClick(post) : null}
                />
              ))
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', width: '100%' }} data-aos="fade-up" data-aos-delay="300">
                <h3 className="empty-text" style={{ color: 'red' }}>No items found</h3>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CardContainer;